import * as SwitchPrimitives from "@radix-ui/react-switch"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "../../lib/utils"

const switchVariants = cva(
  [
    // Layout
    "inline-flex h-[1.25rem] w-10 shrink-0 justify-center items-center",
    // Appearance
    "rounded-100",
    // States
    "cursor-pointer",
    "transition-colors",
    // Focus visible states
    "focus-visible:outline-none focus-visible:ring-2",
    // Disabled
    "disabled:cursor-not-allowed disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        default: [
          // Base & state colors
          "data-[state=checked]:bg-control-background-color-checked data-[state=unchecked]:bg-control-background-color-subtle-default",
          // Hover states
          "hover:data-[state=unchecked]:bg-control-background-color-subtle-hover",
          "hover:data-[state=checked]:bg-action-secondary-background-hover",
          // Focus visible states
          "focus-visible:data-[state=unchecked]:bg-control-background-color-subtle-hover",
          "focus-visible:data-[state=checked]:bg-action-secondary-background-focus",
          "focus-visible:ring-action-base-border-focus",
          // Active states
          "active:data-[state=unchecked]:bg-control-background-color-subtle-hover",
          "active:data-[state=checked]:bg-action-secondary-background-active",
        ],
        inverse: [
          // Base & state colors for dark backgrounds
          "data-[state=checked]:bg-control-background-color-checked data-[state=unchecked]:bg-action-ghost-inverse-background-hover",
          // Hover states
          "hover:data-[state=unchecked]:bg-action-ghost-inverse-background-focus",
          "hover:data-[state=checked]:bg-action-secondary-background-hover",
          // Focus visible states
          "focus-visible:data-[state=unchecked]:bg-action-ghost-inverse-background-focus",
          "focus-visible:data-[state=checked]:bg-action-secondary-background-focus",
          "focus-visible:ring-root-inverse-text-base",
          // Active states
          "active:data-[state=unchecked]:bg-action-ghost-inverse-background-active",
          "active:data-[state=checked]:bg-action-secondary-background-active",
        ],
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const switchThumbVariants = cva(
  [
    // Layout
    "block h-4 w-4",
    // Appearance
    "pointer-events-none rounded-100 shadow-lg ring-0",
    // Animation
    "transition-transform",
    // States - translation calculated to maintain 0.125rem (2px) space on edges
    "data-[state=checked]:translate-x-[10px] data-[state=unchecked]:translate-x-[-10px]",
  ],
  {
    variants: {
      variant: {
        default: "bg-control-foreground-color-default",
        inverse: "bg-control-foreground-color-default",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
    VariantProps<typeof switchVariants> {}

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, variant, ...props }, ref) => (
    <SwitchPrimitives.Root
      className={cn(switchVariants({ variant }), className)}
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb className={cn(switchThumbVariants({ variant }))} />
    </SwitchPrimitives.Root>
  )
)
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
